import { closeCompletion, startCompletion } from '@codemirror/autocomplete';
import { StreamLanguage } from '@codemirror/language';
import { linter, setDiagnostics } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';

import { EditorLanguageConfiguration } from '../../base-code-editor/configuration/configuration.js';
import type { SuggestionCache } from '../autocomplete/get-suggestions.js';
import { openAutocomplete } from '../autocomplete/open-autocomplete.js';
import { createSuggestionAutocompletion } from '../autocomplete/suggestion-autocompletions.js';
import { suggestionsInfoSectionExtension } from '../autocomplete/suggestions-info-section-extension.js';
import type { DQLEditorValidityInfo } from '../dql-editor-types.js';
import { dqlFoldService } from './dql-fold-service.js';
import { dqlLinter, toDiagnostics } from './dql-linter.js';
import { dql } from './dql.js';

const VALIDATION_DELAY = 500;

/**
 * @internal
 * Gets editor configuration for DQL.
 **/
export function getDqlConfig(
  readOnly: boolean,
  onValidationStart?: () => void,
  onValidationEnd?: (validityInfo: DQLEditorValidityInfo[]) => void,
  setValidityInfo?: (validityInfo: DQLEditorValidityInfo[]) => void,
): EditorLanguageConfiguration {
  const language = StreamLanguage.define(dql);

  // Suggestion cache scoped to this config (and therefore to the editor
  // instance that created it). It is discarded when the editor unmounts, so
  // cached suggestions never outlive the editor or leak between editors.
  const suggestionCache: SuggestionCache = new Map();

  return {
    language,
    extendWith: [
      dqlFoldService,
      language.data.of({
        commentTokens: {
          block: {
            open: '/*',
            close: '*/',
          },
          line: '//',
        },
        closeBrackets: {
          brackets: ['(', '[', '{', '"'],
          before: ')]}:;>|',
          stringPrefixes: [],
        },
      }),
      EditorView.domEventObservers({
        // Clear lint decorations when composition starts. Lint spans in the DOM
        // corrupt CodeMirror's DOM reconciliation during IME / dead-key composition.
        // The linter re-runs automatically after composition ends since the
        // composed character is a document change.
        compositionstart: (_event, view) => {
          closeCompletion(view);
          view.dispatch(setDiagnostics(view.state, []));
        },
      }),
      linter(
        async (view) => {
          const editorContent = view.state.doc.toString();

          const validityResult = await dqlLinter(
            editorContent,
            onValidationStart,
            onValidationEnd,
          );

          if (view.composing) {
            return [];
          }

          setValidityInfo?.(validityResult);
          return toDiagnostics(validityResult);
        },
        {
          delay: VALIDATION_DELAY,
        },
      ),
      EditorView.updateListener.of((update) => {
        const isTyping =
          update.docChanged &&
          update.transactions.some((transaction) =>
            transaction.isUserEvent('input.type'),
          );

        const isDeleting =
          update.docChanged &&
          update.transactions.some((transaction) =>
            transaction.isUserEvent('delete.backward'),
          );

        const isFocused = update.focusChanged && update.view.hasFocus;

        if (!(isTyping || isDeleting || isFocused)) {
          return;
        }

        if (update.view.composing) {
          return;
        }

        void openAutocomplete(
          {
            isTyping,
            isDeleting,
            isFocused,
          },
          update.state,
        ).then((shouldComplete) => {
          if (shouldComplete) {
            startCompletion(update.view);
          }
        });
      }),
      ...(readOnly ? [] : [suggestionsInfoSectionExtension]),
    ],
    autocompleteOptions: readOnly
      ? undefined
      : {
          activateOnTyping: false,
          optionClass: (completion) =>
            completion.label.length === 0 ? 'cm-empty-option' : '',
          providers: [createSuggestionAutocompletion(suggestionCache)],
        },
  };
}
